import React, { useMemo } from 'react';
import { DataListSkeletonProps, SkeletonShape } from './types';

/**
 * Get skeleton element based on shape type
 */
const getSkeletonByShape = (
    shape: SkeletonShape | undefined,
    customSkeleton?: () => React.ReactNode,
    skeletonWidth?: string
): React.ReactNode => {
    const width = skeletonWidth || 'w-1/2';

    switch (shape) {
        case 'status':
            return (
                <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-gray-300 dark:bg-gray-600 animate-pulse" />
                    <div className="h-3 w-16 rounded bg-gray-300 dark:bg-gray-600 animate-pulse" />
                </div>
            );
        case 'badge':
            return <div className="h-6 w-16 rounded-full bg-gray-300 dark:bg-gray-600 animate-pulse" />;
        case 'avatar':
            return <div className="w-7 h-7 rounded-full bg-gray-300 dark:bg-gray-600 animate-pulse" />;
        case 'avatarPadded':
            // Avatar with left padding to match Created By column layout
            return (
                <div className="pl-2">
                    <div className="w-7 h-7 rounded-full bg-gray-300 dark:bg-gray-600 animate-pulse" />
                </div>
            );
        case 'avatarGroup':
            return (
                <div className="flex -space-x-2">
                    {[1, 2, 3].map(i => (
                        <div key={i} className="w-8 h-8 rounded-full bg-gray-300 dark:bg-gray-600 border-2 border-gray-200 dark:border-gray-700 animate-pulse" />
                    ))}
                </div>
            );
        case 'workItem':
            // Matches Work Item column: w-6 spacer (for expand chevron) + icon (w-9 h-9) + text lines
            return (
                <div className="flex items-center gap-2">
                    {/* Spacer matching the expand chevron area in DataListRow */}
                    <div className="w-6 flex-shrink-0" />
                    <div className="flex items-start gap-1 flex-1 min-w-0">
                        <div className="w-9 h-9 rounded-md bg-gray-300 dark:bg-gray-600 animate-pulse flex-shrink-0" />
                        <div className="flex flex-col min-w-0 flex-1 gap-1">
                            <div className="h-4 w-3/4 rounded bg-gray-300 dark:bg-gray-600 animate-pulse" />
                            <div className="h-2 w-16 rounded bg-gray-300 dark:bg-gray-600 animate-pulse" />
                        </div>
                    </div>
                </div>
            );
        case 'progress':
            return (
                <div className="flex items-center justify-center">
                    <div className="w-9 h-9 rounded-full border-[3px] border-gray-300 dark:border-gray-600 animate-pulse flex items-center justify-center">
                        <div className="h-2 w-4 rounded bg-gray-300 dark:bg-gray-600" />
                    </div>
                </div>
            );
        case 'action':
            return (
                <div className="flex items-center justify-center w-full">
                    <div className="h-5 w-5 rounded bg-gray-300 dark:bg-gray-600 animate-pulse" />
                </div>
            );
        case 'custom':
            return customSkeleton ? customSkeleton() : <div className={`h-3 ${width} rounded bg-gray-300 dark:bg-gray-600 animate-pulse`} />;
        case 'text':
        default:
            return <div className={`h-3 ${width} rounded bg-gray-300 dark:bg-gray-600 animate-pulse`} />;
    }
};

/**
 * DataListSkeleton - Loading skeleton for DataList
 * Shows animated placeholder rows while data is loading
 * Supports sticky action column on the right
 */
const DataListSkeleton: React.FC<DataListSkeletonProps> = ({
    columns,
    visibleColumns,
    columnWidths,
    rowCount = 8,
    rowHeight = 56,
    stickyColumns = ['action'],
}) => {
    // Separate visible columns into scrollable and sticky
    const { scrollableCols, stickyCols } = useMemo(() => {
        const visibleCols = columns.filter(col => visibleColumns[col.key]);
        const sticky = visibleCols.filter(col => stickyColumns.includes(col.key));
        const scrollable = visibleCols.filter(col => !stickyColumns.includes(col.key));
        return { scrollableCols: scrollable, stickyCols: sticky };
    }, [columns, visibleColumns, stickyColumns]);

    const rows = Array.from({ length: rowCount }, (_, i) => i);

    return (
        <>
            {rows.map((_, rowIndex) => (
                <tr
                    key={rowIndex}
                    className="w-full border-b border-light-border-subtle dark:border-gray-700/60 bg-secondary-light dark:bg-secondary-dark"
                    style={{ height: `${rowHeight}px` }}
                >
                    {/* Scrollable skeleton columns */}
                    {scrollableCols.map(col => (
                        <td
                            key={col.key}
                            className="px-4 py-3 align-middle"
                            style={{ width: columnWidths[col.key] }}
                        >
                            {getSkeletonByShape(col.skeletonShape, col.customSkeleton, col.skeletonWidth)}
                        </td>
                    ))}

                    {/* Sticky skeleton columns */}
                    {stickyCols.map(col => (
                        <td
                            key={col.key}
                            className="px-4 py-3 sticky right-0 z-20 bg-secondary-light dark:bg-secondary-dark align-middle"
                            style={{ 
                                width: columnWidths[col.key],
                                boxShadow: '-4px 0 8px -4px rgba(0,0,0,0.08)',
                            }}
                        >
                            {getSkeletonByShape(col.skeletonShape, col.customSkeleton, col.skeletonWidth)}
                        </td>
                    ))}
                </tr>
            ))}
        </>
    );
};

export default DataListSkeleton;
